import React from 'react';
import logger from '../../../../utils/logger';

export const isMasterPetitionCase = (caseItem) =>
  caseItem?.MasterPetition === 1 || caseItem?.MasterPetition === true;

/** BAR-filed cases and master petitions are live. Anything else is not in play. */
export const isLiveFiledCase = (caseItem) =>
  isMasterPetitionCase(caseItem) || caseItem?.BARFiled === 1;

/** BAR filed, and SCAR has not been filed or already flagged off. */
export const canMarkDoNotFileScar = (caseItem) => {
  if (!caseItem || isMasterPetitionCase(caseItem)) return false;
  const barFiled = caseItem.BARFiled === 1 || caseItem.BARFiled === '1' || caseItem.barFiled === true;
  if (!barFiled) return false;
  const scarFiled = caseItem.SCARFiled;
  return scarFiled == null || scarFiled === '';
};

export const isAssignmentComplete = (caseItem, reviewType) =>
  !!caseItem?.CaseAssignmentInfo?.find((info) => info.ReviewType === reviewType)?.DateCompleted;


// Helper function to determine case progression stage
export function getCaseProgressionStage(caseItem) {
  if (caseItem.SCARFiled === 0) return 'BAR';
  // SCARFiled === 1 or null/undefined means will proceed to SCAR
  return 'SCAR';
}

// Helper to render tooltips with consistent <br/> handling
export function renderUnifiedTooltip(lines) {
  return (
    <div>
      {lines.flatMap((line, i) => {
        if (typeof line === 'string') {
          // Split on both \n and <br/>
          return line
            .replace(/<br\s*\/?>(?![\s\S]*<br\s*\/?)/gi, '\n') // treat <br/> as \n for splitting
            .split(/\n|<br\s*\/?>(?![\s\S]*<br\s*\/?)/gi)
            .map((l, j) => <div key={i + '-' + j}>{l}</div>);
        } else if (React.isValidElement(line)) {
          // If the element is a <br/>, render as a line break
          if (line.type === 'br') {
            return <div key={i + '-br'} style={{ height: 0 }} />;
          }
          // If the element contains <br/> as children, flatten them
          if (line.props && line.props.children) {
            const children = Array.isArray(line.props.children) ? line.props.children : [line.props.children];
            return children.flatMap((child, k) => {
              if (typeof child === 'string') {
                return child.split(/\n|<br\s*\/?>(?![\s\S]*<br\s*\/?)/gi).map((l, m) => <div key={i + '-' + k + '-' + m}>{l}</div>);
              } else if (React.isValidElement(child) && child.type === 'br') {
                return <div key={i + '-' + k + '-br'} style={{ height: 0 }} />;
              } else {
                return <div key={i + '-' + k}>{child}</div>;
              }
            });
          }
          return <div key={i}>{line}</div>;
        } else {
          return <div key={i}>{line}</div>;
        }
      })}
    </div>
  );
}

// Helper function to determine case stage
export const getCaseStage = (caseItem) => {
  const { BARFiled } = caseItem;
  const progressionStage = getCaseProgressionStage(caseItem);
  if (progressionStage === 'SCAR') {
    return 'SCAR';
  }
  if (progressionStage === 'BAR' && BARFiled === 1) {
    return 'BAR';
  }
  return 'Unsettled';
};

// Helper to generate filename for export
export const generateFileName = (MuniCode, taxYear, courtDate) => {
  return `${MuniCode}_${taxYear}${courtDate === 'All' ? '' : `_${courtDate}`}`;
};

// municode_year_evidence, or municode_year_evidence_courtdate when a court date is set
export const generateEvidenceFileName = (MuniCode, taxYear, courtDate) => {
  const court = !courtDate || courtDate === 'All'
    ? ''
    : `_${String(courtDate).trim().replace(/[\\/:*?"<>|\s]+/g, '-')}`;
  const year = taxYear == null || taxYear === '' ? '' : `_${taxYear}`;
  return `${MuniCode || 'Evidence'}${year}_evidence${court}`;
};

// Helper function to check if an offer is sent based on negotiations array
export const isOfferSent = (negotiations, parcelId = null) => {
  if (!negotiations || !Array.isArray(negotiations) || negotiations.length === 0) {
    return false;
  }
  
  // Find the latest round with Aventine offers
  let latestAventineOffer = null;
  let latestRound = 0;
  
  for (let i = 0; i < negotiations.length; i++) {
    const negotiation = negotiations[i];
    
    if (negotiation.round && negotiation.offers && Array.isArray(negotiation.offers)) {
      const aventineOffer = negotiation.offers.find(offer => offer.OfferedBy === 'Aventine');
      
      if (aventineOffer && negotiation.round >= latestRound) {
        latestAventineOffer = aventineOffer;
        latestRound = negotiation.round;
      }
    }
  }
  
  // Check if the latest Aventine offer is live (LiveOffer === 1)
  return latestAventineOffer && latestAventineOffer.LiveOffer === 1;
};

/** True if any negotiation round has an Aventine offer with LiveOffer === 1 */
export const wereAnyOffersSent = (negotiations) => {
  if (!negotiations || !Array.isArray(negotiations) || negotiations.length === 0) {
    return false;
  }
  for (let i = 0; i < negotiations.length; i++) {
    const offers = negotiations[i]?.offers;
    if (!Array.isArray(offers)) continue;
    const aventineOffer = offers.find((o) => o.OfferedBy === 'Aventine');
    if (aventineOffer && aventineOffer.LiveOffer === 1) return true;
  }
  return false;
};

/** Town display omit: workup MV is above EQ (`IFMV`). */
export const isWorkupAboveIfmv = (item) => {
  const raw = item?.ProprietyWorkup ?? item?.SubjectMarketValue;
  const wu = parseFloat(raw);
  if (!Number.isFinite(wu) || wu <= 0) return false;
  const eq = Number(item?.IFMV);
  return Number.isFinite(eq) && eq > 0 && wu > eq;
};

const getLatestAventineOffer = (negotiations) => {
  if (!negotiations || !Array.isArray(negotiations) || negotiations.length === 0) {
    return { offer: null, round: 0 };
  }

  let latestAventineOffer = null;
  let latestRound = 0;

  for (let i = 0; i < negotiations.length; i++) {
    const negotiation = negotiations[i];

    if (negotiation.round && negotiation.offers && Array.isArray(negotiation.offers)) {
      const aventineOffer = negotiation.offers.find((offer) => offer.OfferedBy === 'Aventine');

      if (aventineOffer && negotiation.round >= latestRound) {
        latestAventineOffer = aventineOffer;
        latestRound = negotiation.round;
      }
    }
  }

  return { offer: latestAventineOffer, round: latestRound };
};

// Helper function to check if there's an updated offer (LiveOffer = 0) but not sent
export const hasUpdatedOffer = (negotiations, parcelId = null) => {
  const { offer: latestAventineOffer } = getLatestAventineOffer(negotiations);
  return latestAventineOffer && latestAventineOffer.LiveOffer === 0;
};

const caseHasMeaningfulOfferValue = (caseItem) => {
  const offerValue = caseItem?.OfferValue;
  if (
    offerValue === null ||
    offerValue === undefined ||
    offerValue === 'None' ||
    offerValue === ''
  ) {
    return false;
  }
  const numeric = Number(offerValue);
  return !Number.isNaN(numeric) && numeric !== 0;
};

export const getUnsentAventineOfferCases = (negotiationObj) => {
  const cases = negotiationObj?.cases;
  if (!Array.isArray(cases) || cases.length === 0) {
    return [];
  }
  return cases.filter(
    (caseItem) =>
      isLiveFiledCase(caseItem) &&
      !isCaseSettled(caseItem) &&
      hasUpdatedOffer(caseItem.Negotiations, caseItem.parcel_id) &&
      caseHasMeaningfulOfferValue(caseItem),
  );
};

/** True when any live, unsettled case has a latest Aventine offer that has not been sent to the town. */
export const hasAnyUnsentAventineOffers = (negotiationObj) => {
  return getUnsentAventineOfferCases(negotiationObj).length > 0;
};

export const getUnsentAventineOfferCaseSummaries = (negotiationObj) => {
  return getUnsentAventineOfferCases(negotiationObj).map((caseItem) => {
    const { offer, round } = getLatestAventineOffer(caseItem.Negotiations);
    return {
      parcel_id: caseItem.parcel_id,
      OfferValue: caseItem.OfferValue,
      latestRound: round,
      latestAventineOffer: offer
        ? { Offer: offer.Offer, LiveOffer: offer.LiveOffer }
        : null,
    };
  });
};

export const updateJurisdictionalAssignment = (negotiationObj, flagData) => {
  if (!flagData) {
    return negotiationObj;
  }

  const updatedNegotiationObj = { ...negotiationObj };
  
  if (flagData.flagType === 'DoNotFileSCAR') {
    // Update SCARFiled status for each flagged parcel
    flagData.parcelIds.forEach(parcelId => {
      const caseIndex = updatedNegotiationObj.cases.findIndex(
        (caseItem) => caseItem.parcel_id === parcelId
      );
      
      if (caseIndex !== -1) {
        const currentCase = updatedNegotiationObj.cases[caseIndex];
        
        // Set SCARFiled to 0 (do not file at SCAR)
        updatedNegotiationObj.cases[caseIndex] = {
          ...currentCase,
          SCARFiled: 0
        };
      }
    });
  } else if (flagData.flagType === 'FileAtSCAR') {
    // Update SCARFiled status for each flagged parcel to allow filing at SCAR
    logger.log('Processing FileAtSCAR flag for parcels:', flagData.parcelIds);
    flagData.parcelIds.forEach(parcelId => {
      const caseIndex = updatedNegotiationObj.cases.findIndex(
        (caseItem) => caseItem.parcel_id === parcelId
      );
      
      logger.log(`Found case at index ${caseIndex} for parcel ${parcelId}`);
      
      if (caseIndex !== -1) {
        const currentCase = updatedNegotiationObj.cases[caseIndex];
        logger.log(`Current SCARFiled value for ${parcelId}:`, currentCase.SCARFiled);
        
        // Set SCARFiled to null (allow filing at SCAR)
        updatedNegotiationObj.cases[caseIndex] = {
          ...currentCase,
          SCARFiled: null
        };
        
        logger.log(`Updated SCARFiled to null for ${parcelId}`);
      }
    });
  }
  
  return updatedNegotiationObj;
};

/** Highest `round` in `negotiations` for a single row; 0 if none. */
export const getRowMaxNegotiationRound = (negotiations) => {
  if (!negotiations || !Array.isArray(negotiations)) return 0;
  let maxRound = 0;
  for (const roundData of negotiations) {
    if (roundData.round > maxRound) maxRound = roundData.round;
  }
  return maxRound;
};

/** Highest `round` in `negotiations` that still has at least one offer; 0 if none. */
export const getMaxNegotiationRoundWithOffers = (negotiations) => {
  if (!negotiations || !Array.isArray(negotiations)) return 0;
  let maxRound = 0;
  for (const round of negotiations) {
    if (round.round > maxRound && round.offers?.length > 0) {
      maxRound = round.round;
    }
  }
  return maxRound;
};

/**
 * Determines if a case is settled based on whether the most recent offer values
 * from both Aventine and Town are equal within the most recent negotiation round
 * AND the most recent Aventine offer is live (LiveOffer === 1)
 * @param {Object} caseItem - The case object containing negotiations
 * @returns {boolean} - True if the case is settled (offers are equal and Aventine offer is live)
 */
export const isCaseSettledByOffers = (caseItem) => {
  if (!caseItem?.Negotiations || !Array.isArray(caseItem.Negotiations)) {
    return false;
  }

  // Get the most recent round
  const sortedRounds = [...caseItem.Negotiations].sort((a, b) => (b.round || 0) - (a.round || 0));
  const mostRecentRound = sortedRounds[0];
  
  if (!mostRecentRound?.offers || !Array.isArray(mostRecentRound.offers)) {
    return false;
  }

  // Find offers from both parties in the most recent round
  const aventineOffer = mostRecentRound.offers.find(offer => offer.OfferedBy === 'Aventine');
  const townOffer = mostRecentRound.offers.find(offer => offer.OfferedBy === 'Town');

  // Both parties must have made offers in the most recent round
  if (!aventineOffer || !townOffer) {
    return false;
  }

  // Aventine offer must be live (LiveOffer === 1)
  if (aventineOffer.LiveOffer !== 1) {
    return false;
  }

  // Both offers must have valid numeric values
  const aventineValue = aventineOffer.Offer;
  const townValue = townOffer.Offer;

  if (aventineValue === null || aventineValue === undefined || aventineValue === '' ||
      townValue === null || townValue === undefined || townValue === '') {
    return false;
  }

  // Convert to numbers and check if they're equal
  const aventineNumeric = Number(aventineValue);
  const townNumeric = Number(townValue);

  if (isNaN(aventineNumeric) || isNaN(townNumeric)) {
    return false;
  }

  // Consider offers equal if they match exactly AND Aventine offer is live
  const offersEqual = aventineNumeric === townNumeric;
  
  // Additionally, require that Negotiation case status is true (completed)
  const negotiationCompleted = isAssignmentComplete(caseItem, 'Negotiation');
  
  return offersEqual && negotiationCompleted;
};

/** Determination action AH counts as settled. The case table labels it ADMIN HOLD. */
export const isAdminHoldDetermination = (caseItem) => {
  if (!caseItem) return false;
  if (isMasterPetitionCase(caseItem)) {
    return String(caseItem.Art7DeterminationAction ?? '').trim() === 'AH';
  }
  if (String(caseItem.SCARDeterminationAction ?? '').trim() === 'AH') return true;
  const scarNotFiled = caseItem.SCARFiled === 0 || caseItem.SCARFiled === '0';
  return scarNotFiled && String(caseItem.BARDeterminationAction ?? '').trim() === 'AH';
};

/**
 * Traditional settlement logic based on determination actions.
 * Master Petition settles only from an Art 7 determination action.
 * A settle date, a blank action, or BAR/SCAR fields do not settle an MP case.
 * Awaiting decision (D) stays open. Admin hold (AH) is settled.
 * @param {Object} caseItem - The case object
 * @returns {boolean} - True if the case is settled by traditional means
 */
export const isCaseSettledTraditional = (caseItem) => {
  if (isMasterPetitionCase(caseItem)) {
    const art7Action = String(caseItem?.Art7DeterminationAction ?? '').trim();
    if (!art7Action || art7Action === 'OA' || art7Action === 'D') return false;
    return true;
  }

  if (isAdminHoldDetermination(caseItem)) return true;

  const {
    SCARSettleDate,
    SCARDeterminationDate,
    SCARDeterminationAction,
    BARDeterminationAction,
    // BARDeterminationDate,
    SCARFiled,
    BARFiled,
    BARSettleDate,
  } = caseItem;

  const scarAction = (SCARDeterminationAction == null ? '' : String(SCARDeterminationAction)).trim();
  if ((SCARSettleDate || SCARDeterminationDate) && scarAction !== 'OA' && scarAction !== 'D') {
    return true;
  }
  // BAR settle is terminal (does not proceed to SCAR).
  if (BARSettleDate) return true;
  if (BARFiled === 1 && SCARFiled === 0 && BARDeterminationAction) return true;

  return false;
};

/**
 * Checks if offers are equal (not blank or 0) in the most recent negotiation round
 * @param {Object} caseItem - The case object containing negotiations
 * @returns {string|false} - Returns "OffersEqual" if offers are equal and valid, false otherwise
 */
export const areOffersEqual = (caseItem) => {
  if (!caseItem?.Negotiations || !Array.isArray(caseItem.Negotiations)) {
    return false;
  }

  // Get the most recent round
  const sortedRounds = [...caseItem.Negotiations].sort((a, b) => (b.round || 0) - (a.round || 0));
  const mostRecentRound = sortedRounds[0];
  
  if (!mostRecentRound?.offers || !Array.isArray(mostRecentRound.offers)) {
    return false;
  }

  // Find offers from both parties in the most recent round
  const aventineOffer = mostRecentRound.offers.find(offer => offer.OfferedBy === 'Aventine');
  const townOffer = mostRecentRound.offers.find(offer => offer.OfferedBy === 'Town');

  // Both parties must have made offers in the most recent round
  if (!aventineOffer || !townOffer) {
    return false;
  }

  // Both offers must have valid numeric values (not null, undefined, empty string, or 0)
  const aventineValue = aventineOffer.Offer;
  const townValue = townOffer.Offer;

  if (aventineValue === null || aventineValue === undefined || aventineValue === '' ||
      townValue === null || townValue === undefined || townValue === '') {
    return false;
  }

  // Convert to numbers and check if they're equal
  const aventineNumeric = Number(aventineValue);
  const townNumeric = Number(townValue);

  if (isNaN(aventineNumeric) || isNaN(townNumeric)) {
    return false;
  }

  // Exclude 0 values
  if (aventineNumeric === 0 || townNumeric === 0) {
    return false;
  }

  // Check if offers are equal
  if (aventineNumeric === townNumeric) {
    return "OffersEqual";
  }

  return false;
};

/**
 * Enhanced settlement check that combines both traditional settlement logic
 * (based on determination actions) and offer-based settlement logic
 * @param {Object} caseItem - The case object
 * @returns {boolean} - True if the case is settled by any method
 */
export const isCaseSettled = (caseOrCases) => {
  // Handle array input
  if (Array.isArray(caseOrCases)) {
    return caseOrCases.map(caseItem => isCaseSettled(caseItem));
  }
  
  // Check traditional settlement logic (determination actions)
  const traditionalSettled = isCaseSettledTraditional(caseOrCases);
  
  // Then check offer-based settlement logic
  // const offerSettled = isCaseSettledByOffers(caseOrCases);
  
  // Case is settled if either method indicates settlement
  return traditionalSettled
  // || offerSettled;
  ;
};

/**
 * Gets settlement information for a case -- removed the entire offer based settlement logic
 * @param {Object} caseItem - The case object
 * @returns {Object} - Settlement information including method and values
 */
export const getSettlementInfo = (caseItem) => {
  const traditionalSettled = isCaseSettledTraditional(caseItem);
  // const offerSettled = isCaseSettledByOffers(caseItem);
  
  let settlementMethod = null;
  let aventineOffer = null;
  let townOffer = null;

  const settlementValue = isMasterPetitionCase(caseItem)
    ? (caseItem.Art7DeterminationValue || 0)
    : (caseItem.SCARDeterminationValue || caseItem.BARDeterminationValue || 0);

  // if (offerSettled && caseItem?.Negotiations) {
  //   const sortedRounds = [...caseItem.Negotiations].sort((a, b) => (b.round || 0) - (a.round || 0));
  //   const mostRecentRound = sortedRounds[0];
    
  //   if (mostRecentRound?.offers) {
  //     const aventineOfferObj = mostRecentRound.offers.find(offer => offer.OfferedBy === 'Aventine');
  //     const townOfferObj = mostRecentRound.offers.find(offer => offer.OfferedBy === 'Town');
      
  //     if (aventineOfferObj && townOfferObj) {
  //       aventineOffer = Number(aventineOfferObj.Offer);
  //       townOffer = Number(townOfferObj.Offer);
  //       settlementMethod = 'offers_equal';
  //     }
  //   }
  // }
  
  if (traditionalSettled) {
    settlementMethod = 'determination_action';
  }
  
  return {
    isSettled: traditionalSettled,
    traditionalSettled,
    settlementMethod,
    aventineOffer,
    townOffer,
    settlementValue
  };
};

export const schoolDistrictNameFromValue = (v) => {
  const raw = Array.isArray(v) ? v[0] : v;
  const s = String(raw ?? '').trim();
  return s && s.toLowerCase() !== 'none' ? s : '';
};

export const sumIfmvBySchoolDistrict = (rows = []) => {
  const totals = new Map();
  for (const row of rows) {
    if (!row || row.loading) continue;
    const name = schoolDistrictNameFromValue(row.SDName) || 'Unknown';
    const ifmv = Number(row.IFMV);
    totals.set(name, (totals.get(name) || 0) + (Number.isFinite(ifmv) ? ifmv : 0));
  }
  return [...totals.entries()].sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0])));
};

export const schoolDistrictIfmvTooltipLines = (rows) => {
  const entries = sumIfmvBySchoolDistrict(rows);
  if (!entries.length) return ['No IFMV by school district'];
  const fmt = (n) => `$${Math.round(n).toLocaleString()}`;
  const total = entries.reduce((sum, [, n]) => sum + n, 0);
  return [`Total IFMV — ${fmt(total)}`, '', ...entries.map(([name, sum]) => `${name} — ${fmt(sum)}`)];
}; 